import React, { forwardRef, useEffect, useMemo, Children, createRef } from 'react';
import PropTypes from 'prop-types';
import { Fade, Slide } from '@wds/motion';
import { createMemoStyles, safeRest, defaultFonts, clsx, usePreviousState, uniqueId } from '@wds/utils';
import { useTheme } from 'react-jss';
import { IconX } from '@wds/icons';

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};

  var target = _objectWithoutPropertiesLoose(source, excluded);

  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) {
    arr2[i] = arr[i];
  }

  return arr2;
}

function _arrayWithoutHoles(arr) {
  if (Array.isArray(arr)) return _arrayLikeToArray(arr);
}

function _iterableToArray(iter) {
  if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter);
}

function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}

function _nonIterableSpread() {
  throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

function _toConsumableArray(arr) {
  return _arrayWithoutHoles(arr) || _iterableToArray(arr) || _unsupportedIterableToArray(arr) || _nonIterableSpread();
}

var SIZES = /* #__PURE__ */Object.freeze(['lg', 'md', 'sm']);
/* #__PURE__ */

Object.freeze([].concat(_toConsumableArray(SIZES), ['xs']));
var KEYS = /* #__PURE__ */Object.freeze({
  DOWN: 'ArrowDown',
  END: 'End',
  ESC: 'Escape',
  HOME: 'Home',
  LEFT: 'ArrowLeft',
  PAGE_DOWN: 'PageDown',
  PAGE_UP: 'PageUp',
  RIGHT: 'ArrowRight',
  UP: 'ArrowUp',
  ENTER: 'Enter',
  SPACE: ' ',
  BACKSPACE: 'Backspace',
  TAB: 'Tab'
}); // Deprecated. Moving to e.key

function ownKeys$2(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);

    if (enumerableOnly) {
      symbols = symbols.filter(function (sym) {
        return Object.getOwnPropertyDescriptor(object, sym).enumerable;
      });
    }

    keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread$2(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};

    if (i % 2) {
      ownKeys$2(Object(source), true).forEach(function (key) {
        _defineProperty(target, key, source[key]);
      });
    } else if (Object.getOwnPropertyDescriptors) {
      Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
      ownKeys$2(Object(source)).forEach(function (key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
      });
    }
  }

  return target;
}

var useStyles$1 = createMemoStyles(function (_ref) {
  var spacing = _ref.spacing,
      type = _ref.type;
  return {
    wrenchCloseButton: function wrenchCloseButton(_ref2) {
      var component = _ref2.component;
      var componentSpecificStyles = {
        badge: {
          position: 'relative',
          top: 'none',
          right: 'none',
          padding: 0,
          marginLeft: spacing[3]
        },
        banner: {
          position: 'absolute',
          top: spacing[2],
          right: spacing[2],
          padding: 0
        },
        default: {
          position: 'absolute',
          right: spacing[1],
          top: spacing[1],
          padding: spacing[2]
        }
      };
      return _objectSpread$2(_objectSpread$2({}, componentSpecificStyles[component || 'default']), {}, {
        fontSize: type.fontSize.body,
        display: 'inline-flex',
        justifyContent: 'center',
        alignItems: 'center',
        cursor: 'pointer',
        borderRadius: '0',
        textDecoration: 'none',
        background: 'transparent',
        border: 'none',
        '&:hover': {
          transition: 'none',
          textDecoration: 'none'
        },
        '&:active': {
          transition: 'none',
          outline: 'none'
        }
      });
    }
  };
});
var useStyles$2 = useStyles$1;
var _excluded$2 = ["children", "component", "label", "onClick", "type", "variant"];

var CloseButton = function CloseButton(_ref) {
  _ref.children;
  var component = _ref.component,
      label = _ref.label,
      onClick = _ref.onClick;
  _ref.type;

  var variant = _ref.variant,
      rest = _objectWithoutProperties(_ref, _excluded$2);

  var theme = useTheme();

  var _useStyles = useStyles$2({
    theme: theme,
    component: component
  }),
      wrenchCloseButton = _useStyles.wrenchCloseButton;

  return /*#__PURE__*/React.createElement("button", Object.assign({
    onClick: onClick,
    type: "button",
    className: wrenchCloseButton,
    "aria-label": label
  }, safeRest(rest)), /*#__PURE__*/React.createElement(IconX, {
    title: "close",
    color: variant
  }));
};

CloseButton.displayName = "CloseButton";
CloseButton.propTypes = {
  children: PropTypes.string,
  component: PropTypes.string,
  label: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
  type: PropTypes.string,
  variant: PropTypes.oneOf(['dark', 'light'])
};
CloseButton.defaultProps = {
  children: 'CloseButton',
  component: undefined,
  type: undefined,
  variant: 'light'
};
var CloseButton$1 = CloseButton;
/* eslint-disable jsx-a11y/no-static-element-interactions */

var Overlay = function Overlay(_ref) {
  var className = _ref.className,
      onClick = _ref.onClick;
  return (
    /*#__PURE__*/
    // eslint-disable-next-line testing-library/consistent-data-testid
    React.createElement("div", {
      className: className,
      onClick: onClick,
      "data-testid": "wds-overlay"
    })
  );
};

Overlay.displayName = "Overlay";
Overlay.propTypes = {
  className: PropTypes.string.isRequired,
  onClick: PropTypes.func
};
Overlay.defaultProps = {
  onClick: undefined
};
var Overlay$1 = Overlay;

function ownKeys$1(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);

    if (enumerableOnly) {
      symbols = symbols.filter(function (sym) {
        return Object.getOwnPropertyDescriptor(object, sym).enumerable;
      });
    }

    keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread$1(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};

    if (i % 2) {
      ownKeys$1(Object(source), true).forEach(function (key) {
        _defineProperty(target, key, source[key]);
      });
    } else if (Object.getOwnPropertyDescriptors) {
      Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
      ownKeys$1(Object(source)).forEach(function (key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
      });
    }
  }

  return target;
}

var modalWidth = {
  sm: '443px',
  md: '676px',
  lg: '909px',
  xl: '1300px'
};
var useStyles = createMemoStyles(function (theme) {
  var breakpoints = theme.breakpoints,
      palette = theme.palette,
      border = theme.border,
      shadow = theme.shadow,
      spacing = theme.spacing,
      _theme$type = theme.type,
      fontSize = _theme$type.fontSize,
      fontWeight = _theme$type.fontWeight;

  var setType = function setType(type) {
    return _objectSpread$1({
      color: palette.text.dark,
      lineHeight: 1.5,
      margin: 0
    }, {
      body: {
        fontSize: fontSize.body,
        fontWeight: fontWeight.regular
      },
      sectionTitle: {
        fontSize: fontSize.sectionTitle,
        fontWeight: fontWeight.light
      }
    }[type]);
  };

  var modal = {
    borderRadius: spacing[4],
    headBgColor: palette.background.light,
    headTypeColor: palette.text.dark,
    bodyBgColor: palette.background.accent,
    bodyTypeColor: palette.text.dark,
    footBgColor: palette.background.light,
    footTypeColor: palette.text.dark,
    border: border.dark,
    boxShadow: shadow.space
  };
  return {
    wrenchModal: function wrenchModal(_ref) {
      var elevation = _ref.elevation;
      return {
        overflow: 'auto',
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        pointerEvents: 'none',
        zIndex: elevation || theme.elevation.space
      };
    },
    wrenchModalContainer: _objectSpread$1(_objectSpread$1({}, defaultFonts(theme)), {}, _defineProperty({
      boxSizing: 'border-box',
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: palette.text.light,
      zIndex: function zIndex(_ref2) {
        var elevation = _ref2.elevation;
        return (elevation || theme.elevation.space) + 1;
      },
      display: 'flex',
      flexDirection: 'column',
      overflow: 'auto',
      outline: 'none',
      pointerEvents: 'all'
    }, "@media (min-width: ".concat(breakpoints.sm, "px)"), {
      maxWidth: function maxWidth(_ref3) {
        var size = _ref3.size;
        return size === "xl" ? '96vw' : '80vw';
      },
      maxHeight: function maxHeight(_ref4) {
        var size = _ref4.size;
        return size === "xl" ? '96vh' : '90vh';
      },
      borderRadius: modal.borderRadius,
      boxShadow: modal.boxShadow,
      position: 'relative',
      top: 'auto',
      left: 'auto',
      right: 'auto',
      bottom: 'auto',
      height: 'auto',
      width: function width(_ref5) {
        var size = _ref5.size;
        return modalWidth[size] || modalWidth.sm;
      }
    })),
    wrenchModalOverlayContainer: function wrenchModalOverlayContainer(_ref6) {
      var elevation = _ref6.elevation;
      return {
        zIndex: elevation || theme.elevation.space,
        position: 'relative'
      };
    },
    wrenchModalOverlay: {
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      position: 'fixed',
      backgroundColor: palette.background.overlay
    },
    wrenchModalBody: function wrenchModalBody(_ref7) {
      var padding = _ref7.padding;
      return {
        overflowY: 'auto',
        flexGrow: 1,
        padding: padding && [spacing[4], spacing[6], spacing[6], spacing[6]]
      };
    },
    wrenchModalAddOn: {
      flexGrow: 1,
      marginTop: spacing[3],
      width: '100%',
      minWidth: '33%',
      textAlign: 'right',
      // In the case that an addon is the first child (modal title was omitted)
      // we do not want a margin top
      '& :first-child': {
        marginTop: 0
      }
    },
    wrenchModalHeader: function wrenchModalHeader(_ref8) {
      var stretched = _ref8.stretched;
      return {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        padding: [spacing[6], spacing[6], spacing[2], spacing[6]],
        background: modal.headBgColor,
        flexShrink: 0,
        borderTopLeftRadius: modal.borderRadius,
        borderTopRightRadius: modal.borderRadius,
        flexDirection: stretched ? 'column' : 'row'
      };
    },
    wrenchModalText: {
      fontSize: 24,
      fontWeight: fontWeight.regular,
      color: modal.headTypeColor,
      marginTop: 0,
      marginBottom: spacing[2]
    },
    wrenchModalTitle: {
      flexGrow: 1,
      width: '100%'
    },
    wrenchModalSubText: _objectSpread$1(_objectSpread$1({}, setType('body')), {}, {
      color: modal.headTypeColor,
      margin: [spacing[1], 0, 0]
    }),
    wrenchModalFooter: {
      display: 'flex',
      justifyContent: 'space-between',
      flexShrink: 0,
      padding: spacing[6],
      background: modal.footBgColor,
      borderBottomLeftRadius: modal.borderRadius,
      borderBottomRightRadius: modal.borderRadius,
      borderTop: "1px solid ".concat(palette.background.accent),
      color: modal.footTypeColor
    },
    wrenchModalActions: function wrenchModalActions(_ref9) {
      var align = _ref9.align;
      return _objectSpread$1(_objectSpread$1({
        display: 'flex',
        flexGrow: 1,
        flexWrap: 'wrap'
      }, {
        right: {
          justifyContent: 'flex-end'
        },
        left: {
          justifyContent: 'flex-start'
        }
      }[align]), {}, {
        '&:not([class^="wrenchButtonGroup"]) > button': {
          marginLeft: spacing[2]
        }
      });
    }
  };
});
var _excluded$1 = ["className", "children", "handleClick", "id", "padding"];
var ModalBody = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var className = _ref.className,
      children = _ref.children,
      handleClick = _ref.handleClick,
      id = _ref.id,
      padding = _ref.padding,
      rest = _objectWithoutProperties(_ref, _excluded$1);

  var theme = useTheme();
  var classes = useStyles({
    padding: padding,
    theme: theme
  });
  var classNames = clsx(classes.wrenchModalBody, className);

  var handleKeyDown = function handleKeyDown(e) {
    if (e.key === KEYS.ESC) {
      handleClick();
      e.preventDefault();
    }
  };

  return (
    /*#__PURE__*/
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    React.createElement("section", Object.assign({
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
      tabIndex: "0",
      ref: ref,
      id: id,
      className: classNames,
      onKeyDown: handleKeyDown
    }, safeRest(rest)), children)
  );
});
ModalBody.displayName = 'ModalBody';
ModalBody.propTypes = {
  /**
   * Modal Body content
   */
  children: PropTypes.node.isRequired,

  /**
   * Custom className
   */
  className: PropTypes.string,

  /**
   * On click event handler
   * function (event: object) => void
   */
  handleClick: PropTypes.func,

  /**
   * Modal ID
   */
  id: PropTypes.string,

  /**
   * If true, padding will be applied around Modal body
   */
  padding: PropTypes.bool
};
ModalBody.defaultProps = {
  className: undefined,
  handleClick: undefined,
  id: undefined,
  padding: true
};

var ModalFooter = function ModalFooter(_ref) {
  var align = _ref.align,
      children = _ref.children;
  var theme = useTheme();

  var _modalFooterUseStyles = useStyles({
    align: align,
    theme: theme
  }),
      wrenchModalActions = _modalFooterUseStyles.wrenchModalActions,
      wrenchModalFooter = _modalFooterUseStyles.wrenchModalFooter;

  return /*#__PURE__*/React.createElement("footer", {
    className: wrenchModalFooter
  }, /*#__PURE__*/React.createElement("div", {
    className: wrenchModalActions
  }, children));
};

ModalFooter.displayName = "ModalFooter";
ModalFooter.propTypes = {
  /**
   * Align footer content
   */
  align: PropTypes.oneOf(['left', 'right']),

  /**
   * Modal footer contents
   */
  children: PropTypes.node.isRequired
};
ModalFooter.defaultProps = {
  align: 'left'
};

var ModalHeader = function ModalHeader(_ref) {
  var addOn = _ref.addOn,
      header = _ref.header,
      id = _ref.id,
      subHeader = _ref.subHeader,
      stretched = _ref.stretched;

  if (subHeader && addOn) {
    throw new Error('You cannot have both subHeader and addon');
  } else if (!header && subHeader) {
    throw new Error('You must provide a header if you provide a subHeader');
  }

  var theme = useTheme();

  var _modalHeaderUseStyles = useStyles({
    addOn: addOn,
    stretched: stretched,
    theme: theme
  }),
      wrenchModalAddOn = _modalHeaderUseStyles.wrenchModalAddOn,
      wrenchModalHeader = _modalHeaderUseStyles.wrenchModalHeader,
      wrenchModalSubText = _modalHeaderUseStyles.wrenchModalSubText,
      wrenchModalText = _modalHeaderUseStyles.wrenchModalText,
      wrenchModalTitle = _modalHeaderUseStyles.wrenchModalTitle;

  return /*#__PURE__*/React.createElement("section", {
    className: wrenchModalHeader
  }, /*#__PURE__*/React.createElement("div", {
    className: wrenchModalTitle
  }, header ? /*#__PURE__*/React.createElement("h2", {
    className: wrenchModalText,
    id: id
  }, header) : null, subHeader ? /*#__PURE__*/React.createElement("p", {
    className: wrenchModalSubText
  }, subHeader) : null), addOn ? /*#__PURE__*/React.createElement("div", {
    className: "".concat(wrenchModalAddOn)
  }, addOn) : null);
};

ModalHeader.displayName = "ModalHeader";
ModalHeader.propTypes = {
  /**
   * Addon Modal Header, can be icon, button, text
   */
  addOn: PropTypes.node,

  /**
   * Define header content
   */
  header: PropTypes.node,

  /**
   * Unique identifiter for Modal Header
   */
  id: PropTypes.string,

  /**
   * If true, header content will fit entire width of parent container
   */
  stretched: PropTypes.bool,

  /**
   * Define subheader content
   */
  subHeader: PropTypes.node
};
ModalHeader.defaultProps = {
  addOn: undefined,
  header: undefined,
  id: undefined,
  stretched: false,
  subHeader: undefined
};
var _excluded = ["children", "closeButtonLabel", "dismissible", "onClose", "show", "size", "title", "elevation", "role"];

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);

    if (enumerableOnly) {
      symbols = symbols.filter(function (sym) {
        return Object.getOwnPropertyDescriptor(object, sym).enumerable;
      });
    }

    keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};

    if (i % 2) {
      ownKeys(Object(source), true).forEach(function (key) {
        _defineProperty(target, key, source[key]);
      });
    } else if (Object.getOwnPropertyDescriptors) {
      Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
      ownKeys(Object(source)).forEach(function (key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
      });
    }
  }

  return target;
}

var MODAL_SIZES = ['xl'].concat(_toConsumableArray(SIZES));

var Modal = function Modal(_ref) {
  var children = _ref.children,
      closeButtonLabel = _ref.closeButtonLabel,
      dismissible = _ref.dismissible,
      onClose = _ref.onClose,
      show = _ref.show,
      size = _ref.size,
      title = _ref.title,
      elevation = _ref.elevation,
      role = _ref.role,
      rest = _objectWithoutProperties(_ref, _excluded);

  var modalBodyRef = /*#__PURE__*/createRef();
  var theme = useTheme();
  var prevShow = usePreviousState(show);

  var _useStyles = useStyles({
    size: size,
    elevation: elevation,
    theme: theme
  }),
      wrenchModal = _useStyles.wrenchModal,
      wrenchModalBody = _useStyles.wrenchModalBody,
      wrenchModalContainer = _useStyles.wrenchModalContainer,
      wrenchModalOverlay = _useStyles.wrenchModalOverlay,
      wrenchModalOverlayContainer = _useStyles.wrenchModalOverlayContainer;

  useEffect(function () {
    if (modalBodyRef.current && prevShow !== show) {
      modalBodyRef.current.focus();
    }

    if (typeof document !== 'undefined') {
      var parent = document.getElementsByTagName('html')[0];

      if (parent) {
        if (show) {
          parent.style.overflow = 'hidden';
        } else {
          parent.style.overflow = 'auto';
        }
      }
    }
  }, [modalBodyRef, prevShow, show]);
  var titleId = useMemo(function () {
    return uniqueId('modal-title-');
  }, []);
  var bodyId = useMemo(function () {
    return uniqueId('modal-body-');
  }, []);

  var handleClick = function handleClick() {
    if (dismissible) {
      if (onClose) onClose();
    }
  };
  /**
   * ModalHeader exists show aria-labelledby
   * @type {boolean}
   */


  var hasModalHeader = Children.toArray(children).some(function (_ref2) {
    var type = _ref2.type;
    return type.displayName === 'ModalHeader';
  });
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: wrenchModalOverlayContainer
  }, /*#__PURE__*/React.createElement(Fade, {
    in: show
  }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Overlay$1, {
    onClick: handleClick,
    className: wrenchModalOverlay
  })))), /*#__PURE__*/React.createElement(Slide, {
    in: show,
    direction: "down",
    distance: "near",
    speed: "fast"
  }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", Object.assign({
    className: wrenchModal
  }, safeRest(rest)), /*#__PURE__*/React.createElement("div", {
    className: wrenchModalContainer,
    "aria-label": title,
    "aria-labelledby": hasModalHeader ? titleId : undefined,
    "aria-describedby": bodyId,
    role: role,
    "aria-modal": "true",
    tabIndex: "-1"
  }, dismissible ? /*#__PURE__*/React.createElement(CloseButton$1, {
    variant: "dark",
    onClick: handleClick,
    label: closeButtonLabel
  }) : null, Children.map(children, function (child) {
    if (!React.isValidElement) {
      return null;
    }

    var displayName = child.type.displayName;

    var childProps = {
      ModalHeader: _objectSpread(_objectSpread({}, child.props), {}, {
        id: titleId
      }),
      ModalBody: _objectSpread(_objectSpread({}, child.props), {}, {
        className: wrenchModalBody,
        id: bodyId,
        handleClick: handleClick,
        ref: modalBodyRef
      })
    }[displayName] || _objectSpread({}, child.props);

    return /*#__PURE__*/React.cloneElement(child, _objectSpread({}, childProps));
  }))))));
};

Modal.propTypes = {
  /**
   * Composed of Modal.Header, Modal.Body, Modal.Footer
   */
  children: PropTypes.node.isRequired,

  /**
   * Label for close button on top right of Modal
   */
  closeButtonLabel: PropTypes.node,

  /**
   * If false, X button on top right of Modal will be hidden
   */
  dismissible: PropTypes.bool,

  /**
   * onClose handler
   * function (event: object) => void
   */
  onClose: PropTypes.func,

  /**
   * If true, Modal will be displayed
   */
  show: PropTypes.bool,

  /**
   * Modal size variants
   */
  size: PropTypes.oneOf(MODAL_SIZES),

  /**
   * Define aria title for Modal
   */
  title: PropTypes.string,

  /**
   * If provided, overrides the z-index value of the component
   */
  elevation: PropTypes.number,

  /**
   * If provided, overrides the role of the component
   */
  role: PropTypes.string
};
Modal.defaultProps = {
  closeButtonLabel: 'Close Modal',
  dismissible: true,
  onClose: undefined,
  show: false,
  size: 'sm',
  title: undefined,
  elevation: undefined,
  role: 'dialog'
};
Modal.Body = ModalBody;
Modal.Footer = ModalFooter;
Modal.Header = ModalHeader;
export { Modal };